import { getEventTypeConfig, getEventTypeId, isEventFeatureEnabled } from '../config/eventTypes';

export const EVENT_READINESS_FEATURES = [
  {
    id: 'event_details',
    title: 'Event details',
    path: '/dashboard?action=edit-event',
    required: true,
    completeDetail: 'Event identity and dates are set.',
    pendingDetail: 'Add the event name and start date.',
  },
  {
    id: 'venue_details',
    title: 'Venue details',
    path: '/dashboard?action=edit-event',
    required: true,
    eventTypes: ['birthday'],
    completeDetail: 'The birthday venue is set.',
    pendingDetail: 'Add the birthday venue from event details.',
  },
  {
    id: 'itinerary',
    title: 'Itinerary',
    path: '/dashboard/itinerary',
    required: true,
    completeDetail: 'The guest schedule has at least one complete function.',
    pendingDetail: 'Add a function with its date, time and venue.',
    featureKey: 'showItinerary',
  },
  {
    id: 'guests',
    title: 'Guest list',
    path: '/dashboard/guests',
    required: true,
    completeDetail: 'Guests have been added to the event.',
    pendingDetail: 'Add at least one guest to enable guest access.',
  },
  {
    id: 'contacts',
    title: 'Key contacts',
    path: '/dashboard/contacts',
    completeDetail: 'At least one contact is available to guests.',
    pendingDetail: 'Add a contact guests can reach during the event.',
  },
  {
    id: 'rooms',
    title: 'Rooms & arrivals',
    path: '/dashboard/rooms',
    completeDetail: 'Accommodation inventory has been added.',
    pendingDetail: 'Set up rooms if the event includes accommodation.',
    featureKey: 'showAccommodation',
  },
  {
    id: 'tasks',
    title: 'Tasks',
    path: '/dashboard/tasks',
    completeDetail: 'Event work is being tracked with tasks.',
    pendingDetail: 'Create tasks if you want to track operational work.',
  },
  {
    id: 'vendors',
    title: 'Vendors',
    path: '/dashboard/vendors',
    completeDetail: 'At least one vendor is assigned to the event.',
    pendingDetail: 'Assign vendors when supplier tracking is needed.',
  },
  {
    id: 'budget',
    title: 'Budget & claims',
    path: '/dashboard/budget',
    completeDetail: 'The event budget is configured.',
    pendingDetail: 'Set up a budget if financial tracking is needed.',
  },
  {
    id: 'wishlist',
    title: "Couple's wishlist",
    path: '/dashboard/gifts',
    featureKey: 'showWishlist',
    completeDetail: 'At least one finalized gift is visible.',
    pendingDetail: 'Add and finalize gifts if the couple wants a wishlist.',
  },
  {
    id: 'team',
    title: 'Team management',
    path: '/dashboard/settings',
    completeDetail: 'At least one team member is assigned.',
    pendingDetail: 'Add team members if others will help manage the event.',
  },
  {
    id: 'web_invite',
    title: 'Web Invite',
    path: '/dashboard/e-invite',
    required: true,
    featureKey: 'showWebInvite',
    completeDetail: 'Your web invite is published and ready to share.',
    pendingDetail: 'Create and publish your web invite.',
  },
];

const EVENT_READINESS_OVERRIDES = {
  baby_shower: {
    wishlist: {
      title: 'Baby Registry',
      completeDetail: 'At least one finalized registry gift is visible.',
      pendingDetail: 'Add and finalize gifts for the baby and growing family.',
    },
  },
  birthday: {
    wishlist: {
      title: 'Birthday Wishlist',
      completeDetail: 'At least one finalized birthday gift is visible.',
      pendingDetail: 'Add and finalize gift ideas when the celebrant wants a wishlist.',
    },
  },
};

const status = (complete, count, extra = {}) => ({
  complete: Boolean(complete),
  ...(Number.isFinite(count) ? { count } : {}),
  ...extra,
});

const hasText = (value) => String(value || '').trim().length > 0;
const hasNames = (value) => Array.isArray(value)
  ? value.some(hasText)
  : hasText(value);
const hasVenue = (venue) => hasText(venue?.name) && hasText(venue?.address);

const progressStatus = (checks, detailLabels = {}) => {
  const completed = checks.filter(Boolean).length;
  const total = Math.max(1, checks.length);
  const progressPercent = Math.round((completed / total) * 100);

  return {
    complete: progressPercent === 100,
    progress_percent: progressPercent,
    completed,
    total,
    detail: progressPercent === 100
      ? (detailLabels.complete || 'Everything is set.')
      : (detailLabels.pending || `${completed} of ${total} items set.`),
  };
};

export function getEventDetailsCompletion(event = {}) {
  const eventType = getEventTypeId(event.event_type);
  const checks = [
    hasText(event.name),
    Boolean(event.start_date),
  ];

  if (eventType !== 'birthday') {
    checks.push(hasText(event.description));
  }

  if (eventType === 'wedding') {
    checks.push(hasText(event.bride_name), hasText(event.groom_name));
  } else {
    checks.push(hasNames(event.host_names));
  }

  if (eventType === 'birthday') {
    checks.push(
      hasNames(event.honoree_names),
      Number.isFinite(Number(event.milestone_number)) && Number(event.milestone_number) > 0,
      (event.venues || []).some(hasVenue),
    );
  }

  return progressStatus(checks, {
    complete: 'All event details are set.',
    pending: `${checks.filter(Boolean).length} of ${Math.max(1, checks.length)} event details set.`,
  });
}

export function fallbackDashboardReadiness(event = {}, dashboard = {}) {
  const itinerary = event.itinerary || [];
  const venues = event.venues || [];
  const guestCount = dashboard.rsvp?.invited ?? dashboard.rsvp?.total ?? 0;
  const completeItineraryItems = itinerary.filter((item) => (
    item?.name?.trim()
    && item.date
    && item.start_time?.trim()
    && (item.venue?.name?.trim() || item.venue?.address?.trim())
  )).length;
  const completeVenues = venues.filter(hasVenue).length;
  const eventDetails = getEventDetailsCompletion(event);
  const required = {
    event_details: status(eventDetails.complete, undefined, eventDetails),
  };
  if (isEventFeatureEnabled(event, 'showItinerary')) {
    required.itinerary = status(completeItineraryItems > 0, itinerary.length, {
      progress_percent: completeItineraryItems > 0 ? 100 : 0,
      completed: completeItineraryItems,
      total: Math.max(1, itinerary.length),
      detail: completeItineraryItems > 0
        ? 'At least one function is complete.'
        : `${completeItineraryItems} of ${Math.max(1, itinerary.length)} functions complete.`,
    });
  }
  if (event.event_type === 'birthday') {
    required.venue_details = status(completeVenues > 0, venues.length, {
      progress_percent: completeVenues > 0 ? 100 : 0,
      completed: completeVenues,
      total: Math.max(1, venues.length),
      detail: completeVenues > 0
        ? 'The birthday venue is set.'
        : `${completeVenues} of ${Math.max(1, venues.length)} venues complete.`,
    });
  }
  Object.assign(required, {
    guests: status(guestCount > 0, guestCount, {
      progress_percent: guestCount > 0 ? 100 : 0,
      completed: Math.min(guestCount, 1),
      total: 1,
    }),
  });
  if (event.event_type === 'birthday' && isEventFeatureEnabled(event, 'showWebInvite')) {
    const webInviteComplete = Boolean(
      event.active_web_invite_template_id
      || event.web_invite_published
      || dashboard.web_invite?.active
      || dashboard.web_invite?.published
    );
    required.web_invite = status(webInviteComplete, undefined, {
      progress_percent: webInviteComplete ? 100 : 0,
      completed: webInviteComplete ? 1 : 0,
      total: 1,
    });
  }
  const completedRequired = Object.values(required).filter((item) => item.complete).length;
  const totalRequired = Object.keys(required).length;

  return {
    progress_percent: Math.round((completedRequired / totalRequired) * 100),
    completed_required: completedRequired,
    total_required: totalRequired,
    required,
    features: {
      contacts: status(false, undefined, { progress_percent: 0, completed: 0, total: 1 }),
      rooms: status((dashboard.rooms?.total || 0) > 0, dashboard.rooms?.total || 0, {
        progress_percent: (dashboard.rooms?.total || 0) > 0 ? 100 : 0,
        completed: Math.min(dashboard.rooms?.total || 0, 1),
        total: 1,
      }),
      tasks: status((dashboard.tasks?.total || 0) > 0, dashboard.tasks?.total || 0, {
        progress_percent: (dashboard.tasks?.total || 0) > 0 ? 100 : 0,
        completed: Math.min(dashboard.tasks?.total || 0, 1),
        total: 1,
      }),
      vendors: status(false, undefined, { progress_percent: 0, completed: 0, total: 1 }),
      budget: status(Boolean(dashboard.budget?.configured), undefined, {
        progress_percent: dashboard.budget?.configured ? 100 : 0,
        completed: dashboard.budget?.configured ? 1 : 0,
        total: 1,
      }),
      wishlist: status(false, undefined, { progress_percent: 0, completed: 0, total: 1 }),
      team: status(false, undefined, { progress_percent: 0, completed: 0, total: 1 }),
    },
  };
}

export function getEventReadinessItems(readiness, eventType = 'wedding', event = null) {
  const eventTypeConfig = getEventTypeConfig(eventType);
  const eventContext = event || { event_type: eventType };
  const statuses = {
    ...(readiness?.required || {}),
    ...(readiness?.features || {}),
  };

  return EVENT_READINESS_FEATURES
    .filter((feature) => (
      (!feature.eventTypes || feature.eventTypes.includes(eventType))
      && (!feature.featureKey || (
        ['showItinerary', 'showAccommodation'].includes(feature.featureKey)
          ? isEventFeatureEnabled(eventContext, feature.featureKey)
          : eventTypeConfig.features?.[feature.featureKey]
      ))
    ))
    .map((feature) => {
      const featureStatus = statuses[feature.id] || status(false);
      const configuredFeature = {
        ...feature,
        ...(EVENT_READINESS_OVERRIDES[eventType]?.[feature.id] || {}),
      };
      return {
        ...configuredFeature,
        ...featureStatus,
        detail: featureStatus.detail || (featureStatus.complete
          ? configuredFeature.completeDetail
          : configuredFeature.pendingDetail),
      };
    });
}
